import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography, Alert, Chip } from '@mui/material';
import apiClient from '../api/client';

interface InitiateResponse {
  autoApproved?: boolean;
  needsConsent?: boolean;
  approveRedirect?: string;
  errorRedirect?: string;
  clientId?: string;
  clientName?: string;
  scopes?: string[];
}

const SCOPE_LABELS: Record<string, string> = {
  'mcp:access': 'Read and update your Cadence data via MCP (work items, plans, calendar, drivers, decisions)',
  'offline_access': 'Stay connected even when you\'re offline (refresh access)',
};

export default function OAuthAuthorizePage() {
  const [phase, setPhase] = useState<'loading' | 'consent' | 'error' | 'submitting'>('loading');
  const [data, setData] = useState<InitiateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pull every OAuth query parameter — these came from the connector and must
  // be passed through to the backend untouched.
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const responseType = params.get('response_type') || '';
  const scope = params.get('scope') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.post('/oauth/authorize/initiate', {
          clientId, redirectUri, responseType, scope, state, codeChallenge, codeChallengeMethod,
        });
        if (cancelled) return;
        setData(data);
        if (data.autoApproved && data.approveRedirect) {
          window.location.assign(data.approveRedirect);
          return;
        }
        if (data.errorRedirect) {
          window.location.assign(data.errorRedirect);
          return;
        }
        if (data.needsConsent) {
          setPhase('consent');
          return;
        }
        setError('Unexpected response from authorisation server.');
        setPhase('error');
      } catch (e: any) {
        if (cancelled) return;
        const msg = e?.response?.data?.error_description || e?.response?.data?.error || 'Could not start authorisation.';
        setError(typeof msg === 'string' ? msg : 'Could not start authorisation.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
    // Run once on mount — the OAuth params come from the URL and don't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (approve: boolean) => {
    setPhase('submitting');
    const scopes = (data?.scopes ?? []).join(' ');
    try {
      const path = approve ? '/oauth/authorize/approve' : '/oauth/authorize/deny';
      const { data: result } = await apiClient.post(path, {
        clientId, redirectUri, scope: scopes, state, codeChallenge, codeChallengeMethod,
      });
      if (result?.redirect) {
        window.location.assign(result.redirect);
        return;
      }
      setError('No redirect URL returned.');
      setPhase('error');
    } catch (e: any) {
      const msg = e?.response?.data?.error_description || e?.response?.data?.error || 'Could not record decision.';
      setError(typeof msg === 'string' ? msg : 'Could not record decision.');
      setPhase('error');
    }
  };

  if (phase === 'loading' || phase === 'submitting') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {phase === 'submitting' ? 'Recording decision…' : 'Checking authorisation…'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (phase === 'error') {
    const isUnknownClient = (error || '').toLowerCase().includes('unknown client');
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Authorisation failed.'}</Alert>
        {isUnknownClient && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Diagnostic:</strong> the client the MCP app is using is{' '}
            <code style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{clientId || '(none)'}</code>.
            Cadence has no record of it.
            <br /><br />
            <strong>To fix:</strong> fully quit Claude Desktop (⌘Q on macOS), reopen it,
            then in Settings → Integrations remove the Cadence connector and add it again
            with <code>https://api.stratiumhq.com/mcp</code>. That triggers a fresh
            registration and the next attempt will succeed.
          </Alert>
        )}
        <Button variant="text" onClick={() => { window.location.href = '/'; }}>Back to Cadence</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Connect {data?.clientName || 'this app'} to Cadence?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {data?.clientName || 'A connected app'} is requesting access to your Cadence account.
            Review what it will be able to do before approving.
          </Typography>

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {(data?.scopes ?? []).map(s => (
              <Box key={s} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Chip label={s} size="small" sx={{ mt: 0.25 }} />
                <Typography variant="body2">{SCOPE_LABELS[s] || s}</Typography>
              </Box>
            ))}
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            You can revoke access at any time from Connections → Connected apps.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => decide(false)} color="inherit">Deny</Button>
            <Button onClick={() => decide(true)} variant="contained">Allow</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
